import {
  Component, EventEmitter, Input, Output, ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { forkJoin } from 'rxjs';
import { UploadFileComponent } from '../upload-file/upload-file.component';
import { VerificationComponent } from '../verification/verification.component';
import { CoursesService } from 'src/app/services/courses.service';

@Component({
  selector: 'app-upload-course-documents',
  templateUrl: './upload-course-documents.component.html',
  styleUrls: ['./upload-course-documents.component.scss'],
})
export class UploadCourseDocumentsComponent {
  @Input() courseItemId!: number;
  @Output() closeModal = new EventEmitter<void>();
  @ViewChild(UploadFileComponent) uploadFileComponent!: UploadFileComponent;

  private duplicateDocuments: { assetId: number; fileName: string }[] = [];

  constructor(private dialog: MatDialog,
    private coursesService: CoursesService) { }

  closeModalHandler = () => {
    this.closeModal.emit();
  }

  uploadDocuments = () => {
    if (this.uploadFileComponent && this.uploadFileComponent.hasFilesToUpload()) {
      // Check for duplicate documents before uploading
      this.checkForDuplicateDocuments();
    }
  }

  checkForDuplicateDocuments = () => {
    // Get selected files
    const selectedFiles = this.uploadFileComponent.getSelectedFiles();

    if (!selectedFiles || selectedFiles.length === 0) {
      return;
    }

    // Reset duplicate documents array
    this.duplicateDocuments = [];

    // Fetch existing documents to check for duplicates
    this.uploadFileComponent.fetchExistingDocuments().subscribe({
      next: (existingDocuments) => {
        // Check for duplicate file names and store document info
        const duplicateFiles: string[] = [];
        selectedFiles.forEach((file: File) => {
          const fileName = file.name;
          const existingDoc = existingDocuments.find((doc: any) => doc.fileName === fileName);
          if (existingDoc) {
            duplicateFiles.push(fileName);
            this.duplicateDocuments.push({
              assetId: existingDoc.assetId,
              fileName: existingDoc.fileName,
            });
          }
        });

        this.handleDuplicateCheck(duplicateFiles);
      },
      error: (err) => {
        console.error('Failed to fetch existing documents:', err);
        // If fetch fails, proceed with upload anyway
        this.uploadFileComponent.triggerUpload();
      },
    });
  }

  handleDuplicateCheck = (duplicateFiles: string[]) => {
    // If duplicates found, show confirmation dialog
    if (duplicateFiles.length > 0) {
      const duplicateCount = duplicateFiles.length;
      const fileText = duplicateCount === 1 ? 'file' : 'files';
      const fileNames = duplicateFiles.join(', ');
      const message = duplicateCount === 1
        ? `A document named "${fileNames}" already exists for this course. Would you like to replace it?`
        : `The following ${fileText} already exist for this course: ${fileNames}. Would you like to replace ${duplicateCount === 1 ? 'it' : 'them'}?`;

      const dialogRef = this.dialog.open(VerificationComponent, {
        data: {
          type: 'alert',
          title: 'Replace Existing Document' + (duplicateCount > 1 ? 's' : ''),
          text: message,
          confirmButtonTitle: 'Yes, replace',
          cancelButtonTitle: 'No, cancel',
          confirmButtonClass: 'green',
        },
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result === 'verified') {
          // User confirmed, delete existing documents first, then upload
          this.deleteDuplicateDocumentsAndUpload();
        }
        // If user cancelled, do nothing - they can cancel the modal or try again
      });
    } else {
      // No duplicates, proceed with upload
      this.uploadFileComponent.triggerUpload();
    }
  }

  deleteDuplicateDocumentsAndUpload = () => {
    if (this.duplicateDocuments.length === 0) {
      // No duplicates to delete, proceed with upload
      this.uploadFileComponent.triggerUpload();
      return;
    }

    // Delete all duplicate documents
    const deleteObservables = this.duplicateDocuments.map((doc) =>
      this.coursesService.removeCourseDocument(this.courseItemId, doc.assetId));

    // Wait for all deletions to complete, then proceed with upload
    forkJoin(deleteObservables).subscribe({
      next: () => {
        // All duplicates deleted, proceed with upload
        this.uploadFileComponent.triggerUpload();
      },
      error: (err) => {
        console.error('Failed to delete existing documents:', err);
        // Even if deletion fails, proceed with upload
        // The backend should handle conflicts or the user can manually delete
        this.uploadFileComponent.triggerUpload();
      },
    });
  }

  uploadCompleted = () => {
    console.log('Upload completed');
    this.closeModalHandler();
  }
}
